var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faCircleCheck, faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Stack, TableBody, TableCell, TableRow, TextField, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GENESYS_REGIONS, genesysRegionHost } from '../../../constants/genesysRegions';
import { LIVE_INTEGRATION_TYPES } from '../../../enum';
import { formatIntegrationTypeLabel } from '../../../util/integrationLabel';
import { Autocomplete, ErrorMessageBox, FallbackComponent, FormField, UnControlledFormField } from '../../common';
import { WizardTable } from '../common/WizardTable';
import { MASKED_SECRET, MAX_TEXT_LENGTH } from '../constants';
import { defaultIntegration, useAddClientForm } from './useAddClientForm';
/** Highlighted container that groups one integration's credential inputs. */
var CredentialBox = function (_a) {
  var children = _a.children;
  return _jsx(Box, __assign({
    sx: {
      p: 2,
      bgcolor: 'action.hover',
      borderRadius: 1
    }
  }, {
    children: _jsx(Stack, __assign({
      spacing: 2
    }, {
      children: children
    }))
  }));
};
/** A required, labelled credential input bound to a single integration field. */
var CredentialField = function (_a) {
  var label = _a.label,
    value = _a.value,
    onValueChange = _a.onValueChange,
    errorMessage = _a.errorMessage,
    type = _a.type,
    disabled = _a.disabled,
    readOnly = _a.readOnly,
    placeholder = _a.placeholder,
    select = _a.select,
    endAdornment = _a.endAdornment,
    sx = _a.sx,
    children = _a.children;
  return _jsx(UnControlledFormField, __assign({
    label: label,
    required: true,
    errorMessage: errorMessage
  }, {
    children: _jsx(TextField, __assign({
      fullWidth: true,
      select: select,
      type: type,
      value: value,
      placeholder: placeholder,
      disabled: disabled,
      error: !!errorMessage,
      onChange: function (e) {
        return onValueChange(e.target.value);
      },
      InputProps: readOnly || endAdornment ? {
        readOnly: readOnly,
        endAdornment: endAdornment
      } : undefined,
      sx: sx
    }, {
      children: children
    }))
  }));
};
var AddClientModalStep1 = function (_a) {
  var _b;
  var t = _a.t,
    tIntegration = _a.tIntegration,
    clientFields = _a.clientFields,
    integrationSection = _a.integrationSection,
    connection = _a.connection,
    requestState = _a.requestState;
  var control = clientFields.control,
    industriesData = clientFields.industriesData,
    clientFieldsDisabled = clientFields.clientFieldsDisabled;
  var integration = integrationSection.integration,
    setIntegration = integrationSection.setIntegration,
    setTestResult = integrationSection.setTestResult,
    setConnectionRequired = integrationSection.setConnectionRequired,
    integrationTypes = integrationSection.integrationTypes,
    integrationTypeError = integrationSection.integrationTypeError,
    credentialErrors = integrationSection.credentialErrors,
    setIntegrationTypeError = integrationSection.setIntegrationTypeError,
    isAlreadyConnected = integrationSection.isAlreadyConnected,
    showClientId = integrationSection.showClientId,
    setShowClientId = integrationSection.setShowClientId,
    genesysClientIdValue = integrationSection.genesysClientIdValue,
    showIntegrationSection = integrationSection.showIntegrationSection,
    lockIntegrationType = integrationSection.lockIntegrationType;
  var hasCredentialFields = connection.hasCredentialFields,
    handleConnect = connection.handleConnect,
    handleDisconnect = connection.handleDisconnect,
    handleRetest = connection.handleRetest,
    isTesting = connection.isTesting,
    isRetesting = connection.isRetesting,
    credentialsComplete = connection.credentialsComplete,
    testResult = connection.testResult,
    alertMessage = connection.alertMessage,
    showConnectionRequired = connection.showConnectionRequired;
  var isSaving = requestState.isSaving,
    createData = requestState.createData,
    updateData = requestState.updateData,
    testData = requestState.testData,
    testSaveData = requestState.testSaveData,
    disconnectData = requestState.disconnectData,
    retestData = requestState.retestData;
  var connectionRequiredMessage = t('modal.connectionRequired');
  var connectError = useMemo(function () {
    if ((testResult === null || testResult === void 0 ? void 0 : testResult.status) === 'failure') return {
      message: alertMessage
    };
    if (showConnectionRequired) return {
      message: connectionRequiredMessage
    };
    return undefined;
  }, [testResult === null || testResult === void 0 ? void 0 : testResult.status, alertMessage, showConnectionRequired, connectionRequiredMessage]);
  var updateField = function (key) {
    return function (value) {
      setIntegration(function (prev) {
        var _a;
        return __assign(__assign({}, prev), (_a = {}, _a[key] = value, _a));
      });
      setTestResult(null);
      setConnectionRequired(false);
    };
  };
  var updateName = function (value) {
    setIntegration(function (prev) {
      return __assign(__assign({}, prev), {
        name: value
      });
    });
  };
  return _jsxs(FallbackComponent, __assign({
    requesting: isSaving,
    overlay: true
  }, {
    children: [_jsx(ErrorMessageBox, {
      errors: [createData.error, updateData.error]
    }), _jsxs(Stack, __assign({
      spacing: 2
    }, {
      children: [_jsx(FormField, __assign({
        name: "clientName",
        label: t('modal.clientName'),
        required: true,
        control: control,
        sx: {
          py: 1
        }
      }, {
        children: _jsx(TextField, {
          fullWidth: true,
          placeholder: t('modal.clientNamePlaceholder'),
          disabled: clientFieldsDisabled,
          inputProps: {
            maxLength: MAX_TEXT_LENGTH
          }
        })
      })), _jsx(FormField, __assign({
        name: "industryId",
        label: t('modal.industry'),
        required: true,
        control: control
      }, {
        children: _jsx(Autocomplete, {
          options: ((_b = industriesData.data) !== null && _b !== void 0 ? _b : []).map(function (ind) {
            return {
              id: ind.id,
              name: ind.name
            };
          }),
          placeholder: t('modal.pleaseSelect'),
          disabled: clientFieldsDisabled
        })
      })), showIntegrationSection && _jsxs(_Fragment, {
        children: [_jsx(UnControlledFormField, __assign({
          label: t('modal.integration'),
          required: true,
          errorMessage: integrationTypeError !== null && integrationTypeError !== void 0 ? integrationTypeError : undefined
        }, {
          children: _jsx(Autocomplete, {
            options: integrationTypes.map(function (it) {
              return {
                id: it.value,
                name: it.label
              };
            }),
            placeholder: t('modal.pleaseSelect'),
            value: integration.type,
            onChange: function (value) {
              setIntegration(__assign(__assign({}, defaultIntegration), {
                type: value !== null && value !== void 0 ? value : ''
              }));
              setTestResult(null);
              setIntegrationTypeError(null);
            },
            disabled: lockIntegrationType,
            error: !!integrationTypeError
          })
        })), hasCredentialFields && _jsx(CredentialField, {
          label: t('modal.connectionName'),
          value: integration.name,
          onValueChange: updateName,
          placeholder: t('modal.connectionNamePlaceholder'),
          disabled: isAlreadyConnected
        }), integration.type === 'genesys' && _jsxs(CredentialBox, {
          children: [_jsx(CredentialField, {
            label: tIntegration('genesys.clientId'),
            type: isAlreadyConnected && !showClientId ? 'password' : 'text',
            value: genesysClientIdValue,
            onValueChange: updateField('clientId'),
            placeholder: tIntegration('genesys.clientId'),
            readOnly: isAlreadyConnected,
            endAdornment: isAlreadyConnected ? _jsx(InputAdornment, __assign({
              position: "end"
            }, {
              children: _jsx(IconButton, __assign({
                size: "small",
                onMouseDown: function (e) {
                  return e.preventDefault();
                },
                onClick: function () {
                  return setShowClientId(function (v) {
                    return !v;
                  });
                }
              }, {
                children: _jsx(FontAwesomeIcon, {
                  icon: showClientId ? faEyeSlash : faEye
                })
              }))
            })) : undefined,
            sx: isAlreadyConnected ? {
              '& .MuiInputBase-root': {
                backgroundColor: 'action.disabledBackground',
                color: 'text.disabled'
              }
            } : undefined
          }), _jsx(CredentialField, {
            label: tIntegration('genesys.clientSecret'),
            type: "password",
            value: isAlreadyConnected ? MASKED_SECRET : integration.clientSecret,
            onValueChange: updateField('clientSecret'),
            disabled: isAlreadyConnected
          }), _jsx(UnControlledFormField, __assign({
            label: tIntegration('genesys.region'),
            required: true
          }, {
            children: _jsx(Autocomplete, {
              options: GENESYS_REGIONS.map(function (r) {
                return {
                  id: genesysRegionHost(r),
                  name: tIntegration("genesys.regions.".concat(r.key), {
                    defaultValue: r.name
                  })
                };
              }),
              placeholder: tIntegration('genesys.region'),
              value: integration.region,
              onChange: updateField('region'),
              disabled: isAlreadyConnected
            })
          }))]
        }), integration.type === 'aspect' && _jsxs(CredentialBox, {
          children: [_jsx(CredentialField, {
            label: tIntegration('aspect.apiBaseUrl'),
            value: integration.apiBaseUrl,
            onValueChange: updateField('apiBaseUrl'),
            placeholder: tIntegration('aspect.apiBaseUrlPlaceholder'),
            disabled: isAlreadyConnected,
            errorMessage: credentialErrors.apiBaseUrl
          }), _jsx(CredentialField, {
            label: tIntegration('aspect.tokenUrl'),
            value: integration.tokenUrl,
            onValueChange: updateField('tokenUrl'),
            placeholder: tIntegration('aspect.tokenUrlPlaceholder'),
            disabled: isAlreadyConnected,
            errorMessage: credentialErrors.tokenUrl
          }), _jsx(CredentialField, {
            label: tIntegration('aspect.clientId'),
            value: integration.clientId,
            onValueChange: updateField('clientId'),
            disabled: isAlreadyConnected
          }), _jsx(CredentialField, {
            label: tIntegration('aspect.clientSecret'),
            type: "password",
            value: isAlreadyConnected ? MASKED_SECRET : integration.clientSecret,
            onValueChange: updateField('clientSecret'),
            disabled: isAlreadyConnected
          })]
        }), hasCredentialFields && _jsxs(Stack, __assign({
          direction: "row",
          alignItems: "center",
          justifyContent: "space-between"
        }, {
          children: [_jsxs(Stack, __assign({
            direction: "row",
            spacing: 1,
            alignItems: "center"
          }, {
            children: [_jsx(Button, __assign({
              variant: "outlined",
              color: "error",
              size: "small",
              onClick: handleDisconnect
            }, {
              children: tIntegration('actions.disconnect')
            })), _jsx(Button, __assign({
              variant: "outlined",
              size: "small",
              onClick: handleConnect,
              disabled: isTesting || !credentialsComplete || isAlreadyConnected,
              startIcon: isTesting ? _jsx(CircularProgress, {
                size: 14
              }) : undefined,
              sx: {
                textTransform: 'uppercase'
              }
            }, {
              children: isTesting ? tIntegration('status.testingConnection') : t('modal.connect')
            }))]
          })), isAlreadyConnected && _jsx(Button, __assign({
            variant: "outlined",
            size: "small",
            onClick: handleRetest,
            disabled: isRetesting,
            startIcon: isRetesting ? _jsx(CircularProgress, {
              size: 14
            }) : undefined,
            sx: {
              textTransform: 'uppercase'
            }
          }, {
            children: isRetesting ? tIntegration('status.testingConnection') : t('modal.retest')
          }))]
        })), (testResult === null || testResult === void 0 ? void 0 : testResult.status) === 'success' && _jsx(Alert, __assign({
          severity: "success",
          icon: _jsx(FontAwesomeIcon, {
            icon: faCircleCheck
          }),
          sx: {
            mt: 1,
            border: '1px solid',
            borderColor: 'success.main'
          }
        }, {
          children: alertMessage
        })), _jsx(ErrorMessageBox, {
          scrollTop: false,
          errors: [connectError, testData.error, testSaveData.error, disconnectData.error, retestData.error]
        })]
      })]
    }))]
  }));
};
var AddClientModalStep2 = function (_a) {
  var t = _a.t,
    context = _a.context,
    isLoading = _a.isLoading,
    availableBusinessUnitsData = _a.availableBusinessUnitsData,
    showNoBusinessUnitsAlert = _a.showNoBusinessUnitsAlert,
    businessUnits = _a.businessUnits,
    clientNameValue = _a.clientNameValue,
    allSelected = _a.allSelected,
    someSelected = _a.someSelected,
    toggleSelectAll = _a.toggleSelectAll,
    selected = _a.selected,
    toggleRow = _a.toggleRow,
    importData = _a.importData;
  return _jsxs(FallbackComponent, __assign({
    requesting: isLoading,
    overlay: true
  }, {
    children: [_jsx(ErrorMessageBox, {
      errors: [availableBusinessUnitsData.error]
    }), showNoBusinessUnitsAlert && _jsx(Alert, __assign({
      severity: "info"
    }, {
      children: t('importBusinessUnits.noneAvailable', {
        context: context
      })
    })), businessUnits.length > 0 && _jsxs(Stack, __assign({
      spacing: 1
    }, {
      children: [_jsx(UnControlledFormField, __assign({
        label: t('importBusinessUnits.clientNameLabel'),
        required: true
      }, {
        children: _jsx(TextField, {
          fullWidth: true,
          value: clientNameValue,
          InputProps: {
            readOnly: true
          }
        })
      })), _jsxs(Typography, __assign({
        variant: "subtitle2",
        sx: {
          py: 1
        }
      }, {
        children: [t('importBusinessUnits.selectTitle', {
          context: context
        }), ' ', _jsx(Box, __assign({
          component: "span",
          sx: {
            color: 'error.main'
          }
        }, {
          children: "*"
        }))]
      })), _jsx(WizardTable, __assign({
        stickyHeader: true,
        containerSx: {
          borderRadius: 1,
          maxHeight: 400
        },
        headers: _jsxs(_Fragment, {
          children: [_jsx(TableCell, __assign({
            padding: "checkbox",
            align: "center",
            sx: {
              width: 56
            }
          }, {
            children: _jsx(Checkbox, {
              checked: allSelected,
              indeterminate: someSelected && !allSelected,
              onChange: toggleSelectAll,
              sx: {
                color: 'common.white',
                '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                  color: 'common.white'
                }
              }
            })
          })), _jsx(TableCell, {
            children: t('importBusinessUnits.tableHeader', {
              context: context
            })
          })]
        })
      }, {
        children: _jsx(TableBody, {
          children: businessUnits.map(function (bu) {
            var isChecked = selected.has(bu.externalId);
            return _jsxs(TableRow, __assign({
              hover: true
            }, {
              children: [_jsx(TableCell, __assign({
                padding: "checkbox",
                align: "center"
              }, {
                children: _jsx(Checkbox, {
                  checked: isChecked,
                  onChange: function () {
                    return toggleRow(bu.externalId);
                  }
                })
              })), _jsx(TableCell, {
                children: bu.name
              })]
            }), bu.externalId);
          })
        })
      })), _jsx(Typography, __assign({
        variant: "caption",
        color: "text.secondary"
      }, {
        children: t('importBusinessUnits.selectedCount', {
          count: selected.size
        })
      }))]
    })), _jsx(ErrorMessageBox, {
      errors: [importData.error]
    })]
  }));
};
export var AddClientModal = function (_a) {
  var _b, _c;
  var open = _a.open,
    onClose = _a.onClose,
    onSaved = _a.onSaved,
    configVersionId = _a.configVersionId,
    editClient = _a.editClient,
    _d = _a.mode,
    mode = _d === void 0 ? editClient ? 'edit-details' : 'add-client' : _d,
    targetIntegrationId = _a.targetIntegrationId;
  var t = useTranslation('orgConfig', {
    keyPrefix: 'clients'
  }).t;
  var tIntegration = useTranslation('configureIntegrations').t;
  var tAction = useTranslation('translation', {
    keyPrefix: 'action'
  }).t;
  var theme = useTheme();
  var ALL_INTEGRATION_TYPES = __spreadArray([{
    value: 'none',
    label: t('modal.integrationTypes.none')
  }], LIVE_INTEGRATION_TYPES.map(function (type) {
    return {
      value: type,
      label: t("modal.integrationTypes.".concat(type), {
        defaultValue: formatIntegrationTypeLabel(type)
      })
    };
  }), true);
  var targetIntegrationType = (_c = ((_b = editClient === null || editClient === void 0 ? void 0 : editClient.integrations) !== null && _b !== void 0 ? _b : []).find(function (i) {
    return i.id === targetIntegrationId;
  })) === null || _c === void 0 ? void 0 : _c.integrationType;
  // A client may hold several named connections of the same type
  var integrationTypes;
  if (mode === 'add-integration') {
    integrationTypes = ALL_INTEGRATION_TYPES.filter(function (it) {
      return it.value !== 'none';
    });
  } else if (mode === 'edit-integration') {
    integrationTypes = ALL_INTEGRATION_TYPES.filter(function (it) {
      return it.value === targetIntegrationType;
    });
  } else {
    integrationTypes = ALL_INTEGRATION_TYPES;
  }
  var clientFieldsDisabled = mode === 'add-integration' || mode === 'edit-integration' || mode === 'edit-details' && !!(editClient === null || editClient === void 0 ? void 0 : editClient.isHqClient);
  var showIntegrationSection = mode !== 'edit-details';
  var lockIntegrationType = mode === 'edit-integration';
  var title;
  if (mode === 'add-client') {
    title = t('modal.addTitle');
  } else if (mode === 'edit-details') {
    title = t('modal.editTitle');
  } else if (mode === 'add-integration') {
    title = t('modal.addIntegrationTitle');
  } else {
    title = t('modal.editIntegrationTitle');
  }
  var _e = useAddClientForm({
      open: open,
      onClose: onClose,
      onSaved: onSaved,
      configVersionId: configVersionId,
      editClient: editClient,
      mode: mode,
      targetIntegrationId: targetIntegrationId,
      t: t
    }),
    control = _e.control,
    clientNameValue = _e.clientNameValue,
    submitStep1 = _e.submitStep1,
    step = _e.step,
    setStep = _e.setStep,
    integration = _e.integration,
    setIntegration = _e.setIntegration,
    testResult = _e.testResult,
    setTestResult = _e.setTestResult,
    setConnectionRequired = _e.setConnectionRequired,
    showClientId = _e.showClientId,
    setShowClientId = _e.setShowClientId,
    integrationTypeError = _e.integrationTypeError,
    credentialErrors = _e.credentialErrors,
    setIntegrationTypeError = _e.setIntegrationTypeError,
    hasCredentialFields = _e.hasCredentialFields,
    isAlreadyConnected = _e.isAlreadyConnected,
    credentialsComplete = _e.credentialsComplete,
    showNextButton = _e.showNextButton,
    canSubmitStep1 = _e.canSubmitStep1,
    showConnectionRequired = _e.showConnectionRequired,
    genesysClientIdValue = _e.genesysClientIdValue,
    alertMessage = _e.alertMessage,
    handleConnect = _e.handleConnect,
    handleDisconnect = _e.handleDisconnect,
    handleRetest = _e.handleRetest,
    handleImport = _e.handleImport,
    isSaving = _e.isSaving,
    isTesting = _e.isTesting,
    isLoading = _e.isLoading,
    isImporting = _e.isImporting,
    isDisconnecting = _e.isDisconnecting,
    isRetesting = _e.isRetesting,
    createData = _e.createData,
    updateData = _e.updateData,
    testData = _e.testData,
    testSaveData = _e.testSaveData,
    disconnectData = _e.disconnectData,
    retestData = _e.retestData,
    availableBusinessUnitsData = _e.availableBusinessUnitsData,
    importData = _e.importData,
    industriesData = _e.industriesData,
    businessUnits = _e.businessUnits,
    selected = _e.selected,
    allSelected = _e.allSelected,
    someSelected = _e.someSelected,
    toggleSelectAll = _e.toggleSelectAll,
    toggleRow = _e.toggleRow,
    showNoBusinessUnitsAlert = _e.showNoBusinessUnitsAlert,
    terminologyContext = _e.terminologyContext;
  return _jsxs(Dialog, __assign({
    open: open,
    onClose: function (_event, reason) {
      if (reason !== 'backdropClick') onClose();
    },
    maxWidth: "sm",
    fullWidth: true,
    transitionDuration: {
      enter: theme.transitions.duration.enteringScreen,
      exit: 0
    }
  }, {
    children: [_jsx(DialogTitle, __assign({
      variant: "h2"
    }, {
      children: title
    })), _jsx(DialogContent, {
      children: _jsxs(Box, __assign({
        sx: {
          pt: 1
        }
      }, {
        children: [step === 1 && _jsx(AddClientModalStep1, {
          t: t,
          tIntegration: tIntegration,
          clientFields: {
            control: control,
            industriesData: industriesData,
            clientFieldsDisabled: clientFieldsDisabled,
            editClient: editClient
          },
          integrationSection: {
            integration: integration,
            setIntegration: setIntegration,
            setTestResult: setTestResult,
            setConnectionRequired: setConnectionRequired,
            integrationTypes: integrationTypes,
            integrationTypeError: integrationTypeError,
            credentialErrors: credentialErrors,
            setIntegrationTypeError: setIntegrationTypeError,
            isAlreadyConnected: isAlreadyConnected,
            showClientId: showClientId,
            setShowClientId: setShowClientId,
            genesysClientIdValue: genesysClientIdValue,
            showIntegrationSection: showIntegrationSection,
            lockIntegrationType: lockIntegrationType
          },
          connection: {
            hasCredentialFields: hasCredentialFields,
            handleConnect: handleConnect,
            handleDisconnect: handleDisconnect,
            handleRetest: handleRetest,
            isTesting: isTesting,
            isRetesting: isRetesting,
            credentialsComplete: credentialsComplete,
            testResult: testResult,
            alertMessage: alertMessage,
            showConnectionRequired: showConnectionRequired
          },
          requestState: {
            isSaving: isSaving,
            createData: createData,
            updateData: updateData,
            testData: testData,
            testSaveData: testSaveData,
            disconnectData: disconnectData,
            retestData: retestData
          }
        }), step === 2 && _jsx(AddClientModalStep2, {
          t: t,
          context: terminologyContext,
          isLoading: isLoading,
          availableBusinessUnitsData: availableBusinessUnitsData,
          showNoBusinessUnitsAlert: showNoBusinessUnitsAlert,
          businessUnits: businessUnits,
          clientNameValue: clientNameValue,
          allSelected: allSelected,
          someSelected: someSelected,
          toggleSelectAll: toggleSelectAll,
          selected: selected,
          toggleRow: toggleRow,
          importData: importData
        })]
      }))
    }), _jsxs(DialogActions, {
      children: [_jsx(Button, __assign({
        variant: "outlined",
        onClick: onClose
      }, {
        children: tAction('cancel')
      })), _jsxs(Box, {
        children: [step === 1 && _jsx(Button, __assign({
          variant: "contained",
          onClick: submitStep1,
          disabled: isSaving || isDisconnecting || !canSubmitStep1
        }, {
          children: showNextButton ? tAction('next') : tAction('save')
        })), step === 2 && _jsxs(_Fragment, {
          children: [_jsx(Button, __assign({
            variant: "outlined",
            onClick: function () {
              return setStep(1);
            },
            sx: {
              mr: 1
            }
          }, {
            children: tAction('previous')
          })), _jsx(Button, __assign({
            variant: "contained",
            onClick: handleImport,
            disabled: selected.size === 0 || isImporting,
            startIcon: isImporting ? _jsx(CircularProgress, {
              size: 14
            }) : undefined
          }, {
            children: tAction('save')
          }))]
        })]
      })]
    })]
  }));
};